import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Image,
  ActivityIndicator,
  Alert,
  Modal,
  Linking,
  Platform,
  TextInput,
  KeyboardAvoidingView,
  Pressable,
} from 'react-native';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import Svg, { Path } from 'react-native-svg';
import { getTukarPoinDetail, quoteTukarPoinOngkir, redeemReward } from '../services/rewardsApi';
import { getShippingAddresses } from '../services/shippingAddressApi';
import AppHeader from '../components/AppHeader';
import SpinWheelShippingAddressModal from '../components/SpinWheelShippingAddressModal';
import {
  TUKAR_POIN_COLORS,
  formatPoinNumber,
  formatRp,
  categoryBadge,
  isVoucherReward,
  needsShipping,
  addressLine,
  paymentUrl,
  linkajaQrUrl,
  successCopy,
  claimInfoCopy,
  findRewardSku,
  firstInStockSku,
  needsSkuPick,
  skuActivePoin,
  skuImageUrl,
  skuInStock,
} from '../utils/tukarPoin';

const FALLBACK_IMAGE = require('../../assets/rewards/reward-image-1.png');

const WEB_BTN_RESET = Platform.OS === 'web'
  ? { backgroundColor: 'transparent', borderWidth: 0, boxShadow: 'none', outlineStyle: 'none' }
  : null;

function QrCodeIcon({ size = 20, color = '#475569' }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0 1 3.75 9.375v-4.5ZM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 0 1-1.125-1.125v-4.5ZM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0 1 13.5 9.375v-4.5Z"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M6.75 6.75h.75v.75h-.75v-.75ZM6.75 16.5h.75v.75h-.75v-.75ZM16.5 6.75h.75v.75h-.75v-.75ZM13.5 13.5h.75v.75h-.75v-.75ZM13.5 19.5h.75v.75h-.75v-.75ZM19.5 13.5h.75v.75h-.75v-.75ZM19.5 19.5h.75v.75h-.75v-.75ZM16.5 16.5h.75v.75h-.75v-.75Z"
        fill={color}
      />
    </Svg>
  );
}

function ClubSwitch({ value, onValueChange, disabled }) {
  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityState={{ checked: !!value, disabled: !!disabled }}
      onPress={() => {
        if (!disabled) onValueChange(!value);
      }}
      disabled={disabled}
      hitSlop={8}
      style={[
        styles.clubSwitch,
        value ? styles.clubSwitchOn : styles.clubSwitchOff,
      ]}
    >
      <View style={[styles.clubSwitchThumb, value ? styles.clubSwitchThumbOn : styles.clubSwitchThumbOff]} />
    </Pressable>
  );
}

function WalletLineIcon({ size = 20, color = '#475569' }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M21 12a2.25 2.25 0 0 0-2.25-2.25H15a3 3 0 1 1-6 0H5.25A2.25 2.25 0 0 0 3 12m18 0v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 9m18 0V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v3"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function CloudCheckIcon({ size = 46, color = '#00C16A' }) {
  const petalCount = 8;
  const petal = size * 0.4;
  const radius = size * 0.32;
  const checkBox = Math.round(size * 0.5);
  return (
    <View style={{ width: size, height: size }}>
      {Array.from({ length: petalCount }, (_, i) => {
        const angle = (i / petalCount) * Math.PI * 2;
        return (
          <View
            key={i}
            style={{
              position: 'absolute',
              width: petal,
              height: petal,
              borderRadius: petal / 2,
              backgroundColor: color,
              left: size / 2 + Math.cos(angle) * radius - petal / 2,
              top: size / 2 + Math.sin(angle) * radius - petal / 2,
            }}
          />
        );
      })}
      <View
        style={{
          position: 'absolute',
          left: size * 0.12,
          top: size * 0.12,
          width: size * 0.76,
          height: size * 0.76,
          borderRadius: size,
          backgroundColor: color,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Svg width={checkBox} height={checkBox} viewBox="0 0 24 24">
          <Path
            d="M5 13 L10 18 L19.5 7"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth={5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      </View>
    </View>
  );
}

function MoneyRow({ label, value, tone }) {
  const color = tone === 'plus'
    ? '#22C55E'
    : tone === 'minus'
      ? '#EF4444'
      : tone === 'total'
        ? '#0F172A'
        : '#0F172A';
  const weight = tone === 'total' ? '600' : '500';
  return (
    <View style={styles.moneyRow}>
      <Text style={[styles.moneyLabel, tone === 'total' && styles.moneyLabelTotal]}>{label}</Text>
      <Text style={[styles.moneyValue, { color, fontWeight: weight }]}>{value}</Text>
    </View>
  );
}

export default function TukarPoinShippingScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const rewardId = Number(route.params?.rewardId || 0);
  const [item, setItem] = useState(route.params?.preview || null);
  const [saldoPoin, setSaldoPoin] = useState(Number(route.params?.saldoPoin || 0));
  const [loading, setLoading] = useState(!item);
  const [claiming, setClaiming] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [addressId, setAddressId] = useState(0);
  const [rates, setRates] = useState([]);
  const [rateKey, setRateKey] = useState('');
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [poinOngkirText, setPoinOngkirText] = useState('0');
  const [useShippingInsurance, setUseShippingInsurance] = useState(false);
  const [success, setSuccess] = useState(null);
  const [infoPopup, setInfoPopup] = useState(null);
  const [ratePickerOpen, setRatePickerOpen] = useState(false);
  const [addrListOpen, setAddrListOpen] = useState(false);
  const [addrFormOpen, setAddrFormOpen] = useState(false);
  const [editAddrId, setEditAddrId] = useState(null);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payMethod, setPayMethod] = useState(null);
  const [qrUrl, setQrUrl] = useState('');
  const [addressesReady, setAddressesReady] = useState(false);
  const [shippingNote, setShippingNote] = useState('');
  const [selectedSkuId, setSelectedSkuId] = useState(Number(route.params?.selectedSkuId || 0));
  const openedEmptyForm = useRef(false);

  const voucher = isVoucherReward(item);
  const physical = !!(item && needsShipping(item) && !voucher);
  const selectedSku = findRewardSku(item, selectedSkuId);
  const selectedRate = rates.find((row) => row.id_kurir === rateKey);
  const selectedAddress = addresses.find((row) => Number(row.id) === Number(addressId));
  const itemPoin = skuActivePoin(item, selectedSku);
  const insuranceEnabled = physical && !!item?.asuransi_pengiriman_enabled;
  const shippingInsuranceWajib = Number(selectedSku?.asuransi_kirim ?? item?.product_shipping_insurance) === 1;
  const shippingInsuranceFee = insuranceEnabled ? Number(item?.asuransi_pengiriman_fee || 0) : 0;
  const insuranceTotalRp = useShippingInsurance ? shippingInsuranceFee : 0;
  const ongkirRp = Number(selectedRate?.harga || 0);
  const totalBiayaRp = ongkirRp + insuranceTotalRp;
  const ongkirPoin = Math.ceil(totalBiayaRp / 100);
  const sisaPoinSetelahItem = Math.max(0, Number(saldoPoin || 0) - itemPoin);
  const maxPoinOngkir = Math.min(sisaPoinSetelahItem, ongkirPoin);
  const parsedPoinOngkir = Math.max(0, parseInt(String(poinOngkirText).replace(/\D/g, ''), 10) || 0);
  const poinDigunakan = Math.min(parsedPoinOngkir, maxPoinOngkir);
  const diskonPoinRp = poinDigunakan * 100;
  const sisaOngkirRp = Math.max(0, totalBiayaRp - diskonPoinRp);
  const perluBayar = physical && sisaOngkirRp > 0;
  const showInfo = (title, body) => setInfoPopup({ title, body });

  const loadDetail = useCallback(async () => {
    if (!rewardId) return;
    try {
      const response = await getTukarPoinDetail(rewardId);
      const data = response?.data || {};
      setItem(data);
      if (data.saldo_poin != null) setSaldoPoin(Number(data.saldo_poin));
    } catch (e) {
      Alert.alert('Gagal memuat', e.message || 'Coba lagi.');
    } finally {
      setLoading(false);
    }
  }, [rewardId]);

  useEffect(() => {
    const first = firstInStockSku(item);
    if (!first) return;
    setSelectedSkuId((current) => {
      const cur = findRewardSku(item, current);
      if (cur && skuInStock(cur)) return Number(current);
      return Number(first.id);
    });
  }, [item?.id]);

  const loadAddresses = useCallback(async (preferId) => {
    try {
      const rows = await getShippingAddresses();
      setAddresses(rows);
      setAddressId((current) => {
        const want = Number(preferId || current || 0);
        if (want && rows.some((row) => Number(row.id) === want)) return want;
        const primary = rows.find((row) => Number(row.alamat_utama) === 1) || rows[0];
        return primary ? Number(primary.id) : 0;
      });
      return rows;
    } catch (e) {
      setAddresses([]);
      return [];
    } finally {
      setAddressesReady(true);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      loadDetail();
      loadAddresses();
    }, [loadDetail, loadAddresses]),
  );

  useEffect(() => {
    if (!physical) {
      setUseShippingInsurance(false);
      return;
    }
    if (shippingInsuranceWajib) setUseShippingInsurance(true);
  }, [physical, shippingInsuranceWajib, item?.id, selectedSkuId]);

  useEffect(() => {
    setPoinOngkirText((current) => {
      const n = parseInt(String(current).replace(/\D/g, ''), 10) || 0;
      if (n > maxPoinOngkir) return String(maxPoinOngkir);
      return current === '' ? '0' : String(n);
    });
  }, [maxPoinOngkir]);

  useEffect(() => {
    if (!physical || !addressId) {
      setRates([]);
      setRateKey('');
      setQuoteError('');
      return undefined;
    }
    if (needsSkuPick(item) && selectedSkuId < 1) {
      return undefined;
    }
    let cancelled = false;
    const run = async () => {
      setQuoting(true);
      setQuoteError('');
      try {
        const response = await quoteTukarPoinOngkir(rewardId, {
          id_alamat: addressId,
          id_produk: selectedSkuId || 0,
        });
        if (cancelled) return;
        const nextRates = response?.data?.rates || [];
        setRates(nextRates);
        setRateKey((current) => {
          if (current && nextRates.some((row) => row.id_kurir === current)) return current;
          return nextRates[0]?.id_kurir || '';
        });
      } catch (e) {
        if (cancelled) return;
        setRates([]);
        setRateKey('');
        setQuoteError(e.message || 'Gagal menghitung ongkir');
      } finally {
        if (!cancelled) setQuoting(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [physical, addressId, rewardId, selectedSkuId]);

  useEffect(() => {
    if (!addressesReady) return;
    if (addresses.length === 0 && !openedEmptyForm.current) {
      openedEmptyForm.current = true;
      setEditAddrId(null);
      setAddrFormOpen(true);
    }
  }, [addressesReady, addresses.length]);

  const submitClaim = async (method) => {
    if (claiming) return;
    setClaiming(true);
    setPayModalOpen(false);
    try {
      const pay = perluBayar ? (method || '') : '';
      const extra = {
        id_produk: selectedSkuId || 0,
        id_alamat: addressId,
        kode_kurir: selectedRate?.kode_kurir,
        kode_servis: selectedRate?.kode_servis,
        ongkir: selectedRate?.harga,
        poin_digunakan: poinDigunakan,
        asuransi_pengiriman: useShippingInsurance ? shippingInsuranceFee : 0,
        asuransi_barang: 0,
        metode_pembayaran: pay,
        catatan_customer: shippingNote,
      };
      const response = await redeemReward(rewardId, extra);
      const data = response?.data || {};
      const usedPoin = itemPoin + poinDigunakan;
      const skuUrl = skuImageUrl(item, selectedSku);
      setSuccess({
        message: response?.message || '',
        data,
        voucher,
        nama: item?.nama_reward || '',
        gambar: skuUrl || item?.gambar_url || '',
        kategori: categoryBadge(item)?.label || 'Hadiah Program',
        poinDigunakan: usedPoin,
        sisaPoin: data.saldo_poin != null ? Number(data.saldo_poin) : Math.max(0, Number(saldoPoin || 0) - usedPoin),
      });
      if (data.saldo_poin != null) setSaldoPoin(Number(data.saldo_poin));
      const url = paymentUrl(data);
      if (!linkajaQrUrl(data) && url && data.status_redeem === 'menunggu_bayar_ongkir') {
        try {
          await Linking.openURL(url);
        } catch (e) {
          Alert.alert('Tidak bisa membuka pembayaran', e.message || '');
        }
      }
    } catch (e) {
      setInfoPopup(claimInfoCopy(e.message));
    } finally {
      setClaiming(false);
    }
  };

  const onPressPay = () => {
    if (claiming) return;
    if (!addressId) {
      showInfo('Alamat Diperlukan', 'Pilih atau tambah alamat pengiriman sebelum klaim hadiah fisik.');
      setEditAddrId(null);
      setAddrFormOpen(true);
      return;
    }
    if (quoting) {
      showInfo('Sedang Menghitung Ongkir', 'Tunggu tarif kurir selesai dimuat, lalu klaim lagi.');
      return;
    }
    if (!selectedRate) {
      showInfo('Pilih Kurir', 'Pilih layanan pengiriman sebelum klaim hadiah fisik.');
      return;
    }
    if (perluBayar) {
      // setPayMethod('linkaja');
      setPayModalOpen(true);
      return;
    }
    submitClaim('');
  };

  const closeSuccess = () => {
    setSuccess(null);
    navigation.navigate('MainTabs', { screen: 'TukarPoin' });
  };

  const openHistory = () => {
    setSuccess(null);
    navigation.navigate('TukarPoinHistory', { caller: 'tukar poin' });
  };

  const payNow = async () => {
    const qr = linkajaQrUrl(success?.data);
    if (qr) {
      setQrUrl(qr);
      return;
    }
    const url = paymentUrl(success?.data);
    setSuccess(null);
    if (url) {
      try {
        await Linking.openURL(url);
      } catch (e) {
        Alert.alert('Tidak bisa membuka pembayaran', e.message || '');
      }
    }
    navigation.navigate('TukarPoinHistory', { caller: 'tukar poin' });
  };

  const onAddressSaved = async (addr) => {
    const id = Number(addr?.id || 0);
    setAddrFormOpen(false);
    setEditAddrId(null);
    openedEmptyForm.current = true;
    if (id > 0 && addr && (addr.nama_penerima || addr.detail_alamat || addr.nama_alamat)) {
      setAddresses((rows) => {
        const idx = rows.findIndex((row) => Number(row.id) === id);
        if (idx < 0) return rows;
        const next = rows.slice();
        next[idx] = { ...rows[idx], ...addr, id };
        return next;
      });
      setAddressId(id);
    }
    await loadAddresses(id);
  };

  const payDisabled = claiming || quoting || !addressId || !selectedRate
    || (needsSkuPick(item) && selectedSkuId < 1);
  const skuUrl = skuImageUrl(item, selectedSku);
  const imageSource = skuUrl ? { uri: skuUrl } : FALLBACK_IMAGE;
  const noAddress = addresses.length === 0;

  const header = (
    <AppHeader title="Pengiriman Reward" onBack={() => navigation.goBack()} />
  );

  if (loading && !item) {
    return (
      <SafeAreaView style={styles.safe}>
        {header}
        <View style={styles.center}>
          <ActivityIndicator color="#F59E0B" />
        </View>
      </SafeAreaView>
    );
  }

  if (!item || !physical) {
    return (
      <SafeAreaView style={styles.safe}>
        {header}
        <View style={styles.center}>
          <Text style={styles.empty}>Reward ini tidak perlu pengiriman.</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe}>
      {header}
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={styles.content}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Catatan (opsional)</Text>
            <TextInput
              style={styles.shippingNoteInput}
              value={shippingNote}
              onChangeText={setShippingNote}
              multiline={true}
              numberOfLines={3}
              placeholder="Tambahkan catatan jika diperlukan"
              placeholderTextColor="#94A3B8"
            />
          </View>

          <View style={styles.card}>
            <Text style={styles.cardTitle}>Alamat Pengiriman</Text>
            {selectedAddress ? (
              <View style={styles.addrBody}>
                <View style={styles.addrIcon}>
                  <Ionicons name="location" size={18} color="#06B6D4" />
                </View>
                <View style={styles.addrCopy}>
                  <Text style={styles.addrName}>
                    {selectedAddress.nama_alamat || 'Alamat'}
                    {' • '}
                    {selectedAddress.nama_penerima}
                  </Text>
                  <Text style={styles.addrMeta}>{selectedAddress.no_telp}</Text>
                  <Text style={styles.addrMeta}>{addressLine(selectedAddress)}</Text>
                  {selectedAddress.catatan ? (
                    <Text style={styles.addrNote}>Catatan tambahan: {selectedAddress.catatan}</Text>
                  ) : (
                    <Text style={styles.addrNote}>Catatan tambahan: -</Text>
                  )}
                </View>
              </View>
            ) : (
              <Text style={styles.metaMuted}>Belum ada alamat tersimpan.</Text>
            )}
            <TouchableOpacity
              style={styles.outlineBtn}
              onPress={() => {
                if (noAddress) {
                  setEditAddrId(null);
                  setAddrFormOpen(true);
                  return;
                }
                setAddrListOpen(true);
              }}
            >
              <Text style={styles.outlineBtnText}>{noAddress ? 'Tambah Alamat' : 'Ganti Alamat'}</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.card}>
            <Text style={styles.courierHeading}>Pilih Kurir</Text>
            {quoting ? (
              <ActivityIndicator color="#F59E0B" />
            ) : quoteError ? (
              <Text style={styles.quoteError}>{quoteError}</Text>
            ) : rates.length === 0 ? (
              <Text style={styles.metaMuted}>Pilih alamat untuk menampilkan tarif kurir.</Text>
            ) : (
              <>
                <TouchableOpacity
                  style={styles.courierRow}
                  onPress={() => rates.length > 1 && setRatePickerOpen((open) => !open)}
                  disabled={rates.length < 2}
                >
                  <View style={styles.flex}>
                    <Text style={styles.courierTitle} numberOfLines={2}>
                      {selectedRate
                        ? `${selectedRate.label}${selectedRate.harga_label ? ` (${selectedRate.harga_label})` : ''}`
                        : 'Pilih kurir'}
                    </Text>
                    {selectedRate?.estimasi ? (
                      <Text style={styles.courierSub}>Estimasi tiba {selectedRate.estimasi}</Text>
                    ) : null}
                  </View>
                  {rates.length > 1 ? (
                    <Ionicons name={ratePickerOpen ? 'chevron-up' : 'chevron-down'} size={18} color="#64748B" />
                  ) : null}
                </TouchableOpacity>
                {ratePickerOpen && rates.length > 1 ? (
                  <ScrollView
                    style={styles.rateListScroll}
                    nestedScrollEnabled
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator
                  >
                    {rates.filter((rate) => rate.id_kurir !== rateKey).map((rate) => (
                      <TouchableOpacity
                        key={rate.id_kurir}
                        style={styles.rateOption}
                        onPress={() => {
                          setRateKey(rate.id_kurir);
                          setRatePickerOpen(false);
                        }}
                      >
                        <Text style={styles.courierTitle} numberOfLines={2}>
                          {rate.label}{rate.harga_label ? ` (${rate.harga_label})` : ''}
                        </Text>
                        {rate.estimasi ? (
                          <Text style={styles.courierSub}>Estimasi tiba {rate.estimasi}</Text>
                        ) : null}
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                ) : null}
              </>
            )}

            <View style={styles.infoNote}>
              <Ionicons name="information-circle" size={16} color="#64748B" />
              <Text style={styles.infoNoteText}>
                Pesanan pada Hari Sabtu, Hari Minggu, serta hari libur nasional akan diproses pada hari kerja berikutnya.
              </Text>
            </View>

            {insuranceEnabled ? (
              <View style={[styles.insRow, shippingInsuranceWajib && styles.insRowDisabled]}>
                <ClubSwitch
                  value={useShippingInsurance}
                  onValueChange={setUseShippingInsurance}
                  disabled={shippingInsuranceWajib}
                />
                <Text style={styles.insTitle}>Asuransi Pengiriman</Text>
              </View>
            ) : null}
          </View>

          <View style={styles.pointBanner}>
            <Ionicons name="information-circle" size={18} color="#f7ab2a" />
            <Text style={styles.pointBannerText}>
              Anda masih akan memiliki {formatPoinNumber(sisaPoinSetelahItem)} poin yang dapat digunakan untuk potongan biaya pengiriman.
            </Text>
          </View>

          <View style={styles.poinBox}>
            <Text style={styles.poinLabel}>Gunakan Poin</Text>
            <View style={styles.poinInputRow}>
              <TextInput
                style={styles.poinInput}
                value={poinOngkirText}
                onChangeText={(raw) => {
                  const digits = String(raw).replace(/\D/g, '');
                  if (digits === '') {
                    setPoinOngkirText('0');
                    return;
                  }
                  const n = Math.min(maxPoinOngkir, parseInt(digits, 10) || 0);
                  setPoinOngkirText(String(n));
                }}
                keyboardType="number-pad"
                placeholder="0"
                placeholderTextColor="#94A3B8"
                maxLength={8}
              />
              <Text style={styles.poinSuffix}>Poin</Text>
              <TouchableOpacity
                style={[styles.maxBtn, maxPoinOngkir < 1 && styles.maxBtnDisabled]}
                onPress={() => setPoinOngkirText(String(maxPoinOngkir))}
                disabled={maxPoinOngkir < 1}
              >
                <Text style={styles.maxBtnText}>Max</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.poinFoot}>
              <Text style={styles.poinHint}>Maks. poin {formatPoinNumber(maxPoinOngkir)}</Text>
              <Text style={styles.poinRate}>1 Point = Rp 100</Text>
            </View>
          </View>

          <View style={styles.refundNote}>
            <Ionicons name="information-circle" size={16} color="#2563EB" />
            <Text style={styles.refundNoteText}>
              Untuk refund wajib disertai video unboxing produk (kecuali E-Liquid).
            </Text>
          </View>

          <Text style={styles.summaryTitle}>Ringkasan</Text>
          <MoneyRow
            label="Ongkos Kirim"
            value={selectedRate ? `+ ${formatRp(ongkirRp)}` : '-'}
            tone="plus"
          />
          {insuranceEnabled && useShippingInsurance ? (
            <MoneyRow
              label="Asuransi Pengiriman"
              value={`+ ${formatRp(insuranceTotalRp)}`}
              tone="plus"
            />
          ) : null}
          <MoneyRow label="Sub Total" value={selectedRate ? formatRp(totalBiayaRp) : '-'} />
          {poinDigunakan > 0 ? (
            <MoneyRow label="Diskon Poin" value={`- ${formatRp(diskonPoinRp)}`} tone="minus" />
          ) : null}
          <View style={styles.summaryRule} />
          <MoneyRow
            label="Total Tagihan"
            value={selectedRate ? formatRp(sisaOngkirRp) : '-'}
            tone="total"
          />

          <TouchableOpacity
            style={[styles.payBtn, payDisabled && styles.payBtnDisabled]}
            onPress={onPressPay}
            disabled={claiming}
          >
            {claiming ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.payBtnText}>Bayar Sekarang</Text>
            )}
          </TouchableOpacity>
        </ScrollView>
      </KeyboardAvoidingView>

      <Modal visible={addrListOpen} animationType="slide" transparent onRequestClose={() => setAddrListOpen(false)}>
        <View style={styles.sheetBackdrop}>
          <Pressable style={styles.sheetDismiss} onPress={() => setAddrListOpen(false)} />
          <View style={styles.sheet}>
            <View style={styles.sheetHead}>
              <Text style={styles.sheetTitle}>Ganti Alamat</Text>
              <Pressable
                style={[styles.iconClose, WEB_BTN_RESET]}
                onPress={() => setAddrListOpen(false)}
                hitSlop={12}
              >
                <Ionicons name="close" size={22} color="#64748B" />
              </Pressable>
            </View>
            <ScrollView
              style={styles.addrListScroll}
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
            >
              {addresses.map((row) => {
                const active = Number(row.id) === Number(addressId);
                return (
                  <TouchableOpacity
                    key={row.id}
                    style={[styles.addrPick, active && styles.addrPickActive]}
                    onPress={() => {
                      setAddressId(Number(row.id));
                      setAddrListOpen(false);
                      setRatePickerOpen(false);
                    }}
                  >
                    <View style={styles.rowBetween}>
                      <Text style={styles.addrName}>
                        {row.nama_alamat || 'Alamat'}
                        {Number(row.alamat_utama) === 1 ? ' • Utama' : ''}
                      </Text>
                      {active ? <Ionicons name="checkmark-circle" size={18} color="#D38A0F" /> : null}
                    </View>
                    <Text style={styles.addrMeta}>{row.nama_penerima} · {row.no_telp}</Text>
                    <Text style={styles.addrMeta} numberOfLines={2}>{addressLine(row)}</Text>
                    <TouchableOpacity
                      onPress={() => {
                        setAddrListOpen(false);
                        setEditAddrId(Number(row.id));
                        setAddrFormOpen(true);
                      }}
                    >
                      <Text style={styles.editLink}>Ubah</Text>
                    </TouchableOpacity>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
            <TouchableOpacity
              style={styles.addAddrBtn}
              onPress={() => {
                setAddrListOpen(false);
                setEditAddrId(null);
                setAddrFormOpen(true);
              }}
            >
              <Ionicons name="add-circle-outline" size={18} color="#1E293B" />
              <Text style={styles.addAddrText}>Tambah alamat</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <SpinWheelShippingAddressModal
        visible={addrFormOpen}
        blockDismiss={noAddress}
        editId={editAddrId}
        title={editAddrId ? 'Ubah alamat' : 'Tambah alamat'}
        saveLabel="Simpan alamat"
        subtitle={
          noAddress
            ? 'Kamu belum punya alamat tersimpan. Alamat ini otomatis diset sebagai alamat utama.'
            : editAddrId
              ? 'Perbarui alamat tanpa meninggalkan alur klaim Tukar Poin.'
              : 'Alamat baru bisa dipilih untuk pengiriman reward ini.'
        }
        onDismiss={() => {
          if (noAddress) return;
          setAddrFormOpen(false);
          setEditAddrId(null);
        }}
        onAddressSaved={onAddressSaved}
      />

      <Modal visible={payModalOpen} animationType="fade" transparent onRequestClose={() => setPayModalOpen(false)}>
        <View style={styles.payBackdrop}>
          <Pressable style={styles.sheetDismiss} onPress={() => setPayModalOpen(false)} />
          <View style={styles.paySheet}>
            <View style={styles.sheetHead}>
              <Text style={styles.payTitle}>Pilih Metode Pembayaran</Text>
              <Pressable
                style={[styles.iconClose, WEB_BTN_RESET]}
                onPress={() => setPayModalOpen(false)}
                hitSlop={12}
              >
                <Ionicons name="close" size={22} color="#64748B" />
              </Pressable>
            </View>
            <TouchableOpacity
              style={styles.payMethod}
              onPress={() => {
                setPayMethod('linkaja');
                submitClaim('linkaja');
              }}
              disabled={claiming}
            >
              <View style={styles.payMethodLeft}>
                <View style={styles.payIcon}>
                  <QrCodeIcon size={20} color="#475569" />
                </View>
                <Text style={styles.payMethodText}>QRIS</Text>
              </View>
              <View style={[styles.radio, payMethod === 'linkaja' && styles.radioOn]}>
                {payMethod === 'linkaja' ? <View style={styles.radioDot} /> : null}
              </View>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.payMethod, styles.payMethodLast]}
              onPress={() => {
                setPayMethod('xendit');
                submitClaim('xendit');
              }}
              disabled={claiming}
            >
              <View style={styles.payMethodLeft}>
                <View style={styles.payIcon}>
                  <WalletLineIcon size={20} color="#64748B" />
                </View>
                <Text style={styles.payMethodText}>Transfer</Text>
              </View>
              <View style={[styles.radio, payMethod === 'xendit' && styles.radioOn]}>
                {payMethod === 'xendit' ? <View style={styles.radioDot} /> : null}
              </View>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal visible={!!infoPopup} transparent animationType="fade" onRequestClose={() => setInfoPopup(null)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.infoCard}>
            <Text style={styles.infoTitle}>{infoPopup?.title}</Text>
            <Text style={styles.infoBody}>{infoPopup?.body}</Text>
            <TouchableOpacity style={styles.infoBtn} onPress={() => setInfoPopup(null)}>
              <Text style={styles.infoBtnText}>Mengerti</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal visible={!!success} transparent animationType="fade">
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <TouchableOpacity style={styles.modalClose} onPress={closeSuccess} hitSlop={12}>
                <Ionicons name="close" size={24} color="#94A3B8" />
              </TouchableOpacity>
            </View>
            <View style={styles.modalContent}>
              <View style={styles.successIconWrap}>
                <CloudCheckIcon size={46} color="#00C16A" />
              </View>
              <Text style={styles.modalTitle}>
                {successCopy(success?.data, false).title}
              </Text>
              <Text style={styles.modalBody}>
                {success?.data?.status_redeem === 'menunggu_bayar_ongkir'
                  ? successCopy(success?.data, false).body
                  : (
                    <>
                      {success?.kategori ? `${success.kategori} ` : ''}
                      <Text style={styles.modalBodyBold}>{success?.nama || item?.nama_reward}</Text>
                      {' berhasil ditukarkan dan telah ditambahkan ke riwayat penukaranmu'}
                    </>
                  )}
              </Text>
              {linkajaQrUrl(success?.data) ? (
                <Image source={{ uri: linkajaQrUrl(success?.data) }} style={styles.qrPreview} resizeMode="contain" />
              ) : (
                <Image
                  source={success?.gambar ? { uri: success.gambar } : imageSource}
                  style={styles.modalImage}
                  resizeMode="contain"
                />
              )}
              <View style={styles.modalSummary}>
                <View style={styles.modalSummaryRow}>
                  <Text style={styles.modalSummaryLabel}>Poin digunakan</Text>
                  <Text style={styles.modalSummaryValue}>
                    {formatPoinNumber(success?.poinDigunakan)} poin
                  </Text>
                </View>
                <View style={styles.modalDash} />
                <View style={styles.modalSummaryRow}>
                  <Text style={styles.modalSummaryLabel}>Sisa Poin Kamu</Text>
                  <Text style={styles.modalSummaryRemain}>
                    {formatPoinNumber(success?.sisaPoin ?? saldoPoin)} poin
                  </Text>
                </View>
              </View>
              <View style={styles.modalActions}>
                {success?.data?.status_redeem === 'menunggu_bayar_ongkir' && paymentUrl(success?.data) && !linkajaQrUrl(success?.data) ? (
                  <TouchableOpacity style={styles.modalFilled} onPress={payNow}>
                    <Text style={styles.payBtnText}>Bayar ongkir sekarang</Text>
                  </TouchableOpacity>
                ) : null}
                <TouchableOpacity style={styles.modalOutline} onPress={closeSuccess}>
                  <Text style={styles.modalOutlineText}>Kembali ke Katalog</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.modalFilled} onPress={openHistory}>
                  <Text style={styles.payBtnText}>Lihat Riwayat Penukaran</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={!!qrUrl} transparent animationType="fade" onRequestClose={() => setQrUrl('')}>
        <View style={styles.modalBackdrop}>
          <View style={styles.infoCard}>
            <Text style={styles.infoTitle}>Bayar ongkir via QRIS</Text>
            <Text style={styles.infoBody}>Scan QR ini untuk menyelesaikan pembayaran.</Text>
            {qrUrl ? (
              <Image source={{ uri: qrUrl }} style={styles.qrPreview} resizeMode="contain" />
            ) : null}
            <TouchableOpacity style={styles.infoBtn} onPress={() => setQrUrl('')}>
              <Text style={styles.infoBtnText}>Tutup</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#FFFFFF' },
  flex: { flex: 1 },
  content: { padding: 16, paddingBottom: Platform.OS === 'ios' ? 36 : 24 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 24 },
  empty: { fontFamily: 'Poppins', color: TUKAR_POIN_COLORS.slate, textAlign: 'center' },
  card: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    backgroundColor: '#fff',
  },
  cardTitle: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 14,
    fontWeight: '600',
    color: '#94A3B8',
    marginBottom: 12,
  },
  addrBody: { flexDirection: 'row', gap: 10, marginBottom: 12 },
  addrIcon: { width: 20, alignItems: 'center', paddingTop: 2 },
  addrCopy: { flex: 1, gap: 4 },
  addrName: { fontFamily: 'Poppins-SemiBold', fontSize: 14, fontWeight: '600', color: '#0F172A' },
  addrMeta: { fontFamily: 'Poppins', fontSize: 12, color: '#475569', lineHeight: 18 },
  addrNote: { fontFamily: 'Poppins', fontSize: 11, color: '#94A3B8', marginTop: 2 },
  outlineBtn: {
    borderWidth: 1,
    borderColor: '#1E293B',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignSelf: 'stretch',
    alignItems: 'center',
  },
  outlineBtnText: { fontFamily: 'Poppins-Medium', fontSize: 13, fontWeight: '500', color: '#1E293B' },
  courierHeading: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 14,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 12,
  },
  courierRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    padding: 12,
    backgroundColor: '#F8FAFC',
  },
  courierTitle: { fontFamily: 'Poppins-SemiBold', fontSize: 13, fontWeight: '600', color: '#0F172A' },
  courierSub: { fontFamily: 'Poppins', fontSize: 12, color: '#64748B', marginTop: 4 },
  rateListScroll: {
    marginTop: 8,
    maxHeight: 220,
  },
  rateOption: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
    backgroundColor: '#fff',
  },
  infoNote: {
    marginTop: 12,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  infoNoteText: { flex: 1, fontFamily: 'Poppins', fontSize: 11, color: '#475569', lineHeight: 16 },
  insRow: {
    marginTop: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  insRowDisabled: { opacity: 0.5 },
  insTitle: { fontFamily: 'Poppins-Medium', fontSize: 13, fontWeight: '500', color: '#000000', flex: 1 },
  clubSwitch: {
    width: 30,
    height: 16,
    borderRadius: 8,
    paddingHorizontal: 1.5,
    flexDirection: 'row',
    alignItems: 'center',
  },
  clubSwitchOff: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    justifyContent: 'flex-start',
  },
  clubSwitchOn: {
    backgroundColor: '#0d6efd',
    borderWidth: 1,
    borderColor: '#0d6efd',
    justifyContent: 'flex-end',
  },
  clubSwitchThumb: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  clubSwitchThumbOff: {
    backgroundColor: '#94A3B8',
  },
  clubSwitchThumbOn: {
    backgroundColor: '#FFFFFF',
  },
  pointBanner: {
    backgroundColor: '#FEF3C7',
    borderRadius: 8,
    padding: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  pointBannerText: {
    flex: 1,
    fontFamily: 'Poppins',
    fontSize: 12,
    fontWeight: '400',
    color: '#0F172A',
    lineHeight: 18,
  },
  poinBox: {
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    padding: 8,
    marginBottom: 12,
  },
  poinLabel: {
    fontFamily: 'Poppins-Medium',
    fontSize: 13,
    fontWeight: '500',
    color: '#000000',
    marginBottom: 8,
  },
  poinRate: { fontFamily: 'Poppins-Medium', fontSize: 13, fontWeight: '500', color: '#F59E0B' },
  poinInputRow: { flexDirection: 'row', alignItems: 'center' },
  poinInput: {
    flex: 1,
    width: '100%',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    backgroundColor: '#fff',
    paddingHorizontal: 12,
    paddingVertical: Platform.OS === 'ios' ? 10 : 8,
    fontFamily: 'Poppins-Medium',
    fontSize: 14,
    color: '#0F172A',
    textAlign: 'right',
    height: 40,
  },
  poinSuffix: {
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#fff',
    paddingHorizontal: 10,
    paddingVertical: Platform.OS === 'ios' ? 10 : 8,
    fontFamily: 'Poppins-Medium',
    fontSize: 13,
    color: '#64748B',
    height: 40,
  },
  maxBtn: {
    backgroundColor: '#F59E0B',
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  maxBtnDisabled: { opacity: 0.5 },
  maxBtnText: { color: '#fff', fontFamily: 'Poppins-SemiBold', fontSize: 13, fontWeight: '600' },
  poinHint: { fontFamily: 'Poppins-Medium', fontSize: 11, color: '#94A3B8' },
  poinFoot: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 6,
    gap: 8,
  },
  refundNote: {
    backgroundColor: '#EFF6FF',
    borderRadius: 8,
    padding: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  refundNoteText: { flex: 1, fontFamily: 'Poppins', fontSize: 11, color: '#2563EB', lineHeight: 16 },
  summaryTitle: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 12,
  },
  moneyRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  moneyLabel: { fontFamily: 'Poppins-Medium', fontSize: 13, fontWeight: '500', color: '#94A3B8' },
  moneyLabelTotal: { color: '#94A3B8' },
  moneyValue: { fontFamily: 'Poppins-Medium', fontSize: 13 },
  summaryRule: { height: 1, backgroundColor: '#F1F5F9', marginVertical: 8 },
  payBtn: {
    backgroundColor: '#1E293B',
    borderRadius: 8,
    paddingVertical: 10,
    alignItems: 'center',
    marginTop: 8,
  },
  payBtnDisabled: { opacity: 0.5 },
  payBtnText: { color: '#fff', fontFamily: 'Poppins-Medium', fontSize: 14, fontWeight: '500' },
  metaMuted: { fontFamily: 'Poppins', fontSize: 12, color: TUKAR_POIN_COLORS.slate, marginBottom: 12 },
  quoteError: { fontFamily: 'Poppins', fontSize: 12, color: '#E11D48', marginBottom: 8 },
  sheetBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(15,23,42,0.45)',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  sheetDismiss: {
    ...StyleSheet.absoluteFillObject,
  },
  sheet: {
    width: '100%',
    maxWidth: 430,
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: Platform.OS === 'ios' ? 28 : 16,
    maxHeight: '80%',
    zIndex: 1,
  },
  addrListScroll: {
    maxHeight: 360,
    marginBottom: 4,
  },
  payBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(15,23,42,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  paySheet: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: '#fff',
    borderRadius: 16,
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 8,
    zIndex: 1,
  },
  sheetHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  sheetTitle: { fontFamily: 'Poppins-SemiBold', fontSize: 16, fontWeight: '600', color: '#0F172A' },
  payTitle: { fontFamily: 'Poppins-SemiBold', fontSize: 18, fontWeight: '600', color: '#0F172A', flex: 1, paddingRight: 12 },
  iconClose: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  addrPick: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
    gap: 2,
  },
  addrPickActive: { borderColor: TUKAR_POIN_COLORS.gold, backgroundColor: '#FFFBEB' },
  editLink: { fontFamily: 'Poppins-SemiBold', fontSize: 12, color: '#2563EB', marginTop: 6 },
  addAddrBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 10,
    padding: 12,
    marginTop: 4,
  },
  addAddrText: { fontFamily: 'Poppins-Medium', color: TUKAR_POIN_COLORS.navy },
  payMethod: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
    gap: 12,
  },
  payMethodLast: { borderBottomWidth: 0 },
  payMethodLeft: { flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 },
  payIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#F8FAFC',
    alignItems: 'center',
    justifyContent: 'center',
  },
  payMethodText: { flex: 1, fontFamily: 'Poppins-Medium', fontSize: 14, color: '#64748B' },
  radio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#CBD5E1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOn: { borderColor: '#1E293B' },
  radioDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#1E293B',
  },
  rowBetween: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  infoCard: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
  },
  infoTitle: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    textAlign: 'center',
  },
  infoBody: {
    fontFamily: 'Poppins',
    fontSize: 13,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 20,
  },
  infoBtn: {
    backgroundColor: '#1E293B',
    borderRadius: 8,
    paddingVertical: 10,
    alignItems: 'center',
    marginTop: 20,
  },
  infoBtnText: { color: '#fff', fontFamily: 'Poppins-Medium', fontSize: 14, fontWeight: '500' },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(15,23,42,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 12,
    paddingVertical: 24,
  },
  modalCard: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
    backgroundColor: '#fff',
    borderRadius: 20,
    overflow: 'hidden',
  },
  modalHeader: { paddingTop: 16, paddingHorizontal: 16, paddingBottom: 32, alignItems: 'flex-end' },
  modalClose: { width: 24, height: 24, alignItems: 'center', justifyContent: 'center' },
  modalContent: { paddingHorizontal: 16, paddingBottom: 16, alignItems: 'stretch' },
  successIconWrap: { alignSelf: 'center', marginBottom: 16 },
  modalTitle: {
    fontFamily: 'Poppins-Bold',
    fontSize: 14,
    fontWeight: '700',
    color: '#00C16A',
    textAlign: 'center',
  },
  modalBody: {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 18,
  },
  modalBodyBold: { fontFamily: 'Poppins-Bold', fontWeight: '700', color: '#334155' },
  modalImage: { width: '100%', height: 170, marginTop: 16 },
  qrPreview: {
    width: 180,
    height: 180,
    alignSelf: 'center',
    marginTop: 12,
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
  },
  modalSummary: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginTop: 16,
    marginBottom: 16,
  },
  modalActions: { gap: 12 },
  modalSummaryRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  modalSummaryLabel: { fontFamily: 'Poppins', fontSize: 14, color: '#64748B' },
  modalSummaryValue: { fontFamily: 'Poppins-Medium', fontSize: 14, fontWeight: '500', color: '#64748B' },
  modalSummaryRemain: { fontFamily: 'Poppins-SemiBold', fontSize: 16, fontWeight: '600', color: '#334155' },
  modalDash: { borderBottomWidth: 1, borderStyle: 'dashed', borderColor: '#CBD5E1', marginVertical: 10 },
  modalOutline: {
    borderWidth: 1,
    borderColor: '#64748B',
    borderRadius: 8,
    paddingVertical: 8,
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  modalOutlineText: { color: '#64748B', fontFamily: 'Poppins-Medium', fontSize: 14, fontWeight: '500' },
  modalFilled: { backgroundColor: '#1E293B', borderRadius: 8, paddingVertical: 8, alignItems: 'center' },
  shippingNoteInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    backgroundColor: '#fff',
    paddingHorizontal: 12,
    paddingVertical: Platform.OS === 'ios' ? 10 : 8,
    fontFamily: 'Poppins-Medium',
    fontSize: 14,
    color: '#0F172A',
  }
});
